import api from "./api";
import { getApiUrl } from "./config";
import { endpoint } from "./endpoints";
import { handleApiCall } from "../utils/apiResponseHandler";

interface PhoneValidationResult {
  phone: string;
  isValid: boolean;
  isUSValid?: boolean;
  areaCode?: string | null;
  formatted?: string | null;
  sanitized?: string;
  error?: string;
}

interface PhoneSanitizeResponse {
  success: boolean;
  data?: {
    original: string;
    sanitized: string;
    format: string;
  };
  error?: string;
}

interface PhoneValidateResponse {
  success: boolean;
  data?: PhoneValidationResult & {
    isUSValid: boolean;
    areaCode: string | null;
  };
  error?: string;
}

interface PhoneFormatResponse {
  success: boolean;
  data?: {
    original: string;
    formatted: string; // (555) 123-4567
    e164: string; // +15551234567
    areaCode: string | null;
  };
  error?: string;
}

interface PhoneCompareResponse {
  success: boolean;
  data?: {
    phone1: string;
    phone2: string;
    areEqual: boolean;
    phone1E164: string;
    phone2E164: string;
  };
  error?: string;
}

interface PhoneValidateUSResponse {
  success: boolean;
  data?: {
    original: string;
    isValidUS: boolean;
    sanitized: string;
    formatted: string;
    areaCode: string | null;
    validAreaCode: boolean;
  };
  error?: string;
}

interface PhoneBatchValidateResponse {
  success: boolean;
  data?: {
    summary: {
      total: number;
      valid: number;
      validUS: number;
      invalid: number;
    };
    results: PhoneValidationResult[];
  };
  error?: string;
}

interface PhoneAreaCodeResponse {
  success: boolean;
  data?: {
    phone: string;
    areaCode: string | null;
  };
  error?: string;
}

/**
 * Service for backend phone number utilities
 * Provides consistent phone number validation, formatting, and sanitization
 * using backend phone-number.util.ts functions
 */
class PhoneUtilsService {
  
  /**
   * Sanitize phone number to E.164 format using backend utilities
   */
  async sanitizePhone(phone: string): Promise<PhoneSanitizeResponse> {
    try {
      const response = await handleApiCall<PhoneSanitizeResponse>(async () => {
        return await api.post({
          url: getApiUrl(endpoint.phoneUtils.sanitize),
          body: { phone },
          auth: true,
        });
      });
      
      return response;
    } catch (error) {

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to sanitize phone number'
      };
    }
  }

  /**
   * Validate phone number using backend utilities
   */
  async validatePhone(phone: string): Promise<PhoneValidateResponse> {
    try {
      const response = await handleApiCall<PhoneValidateResponse>(async () => {
        return await api.post({
          url: getApiUrl(endpoint.phoneUtils.validate),
          body: { phone },
          auth: true,
        });
      });
      
      return response;
    } catch (error) {

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to validate phone number'
      };
    }
  }

  /**
   * Format phone number for display using backend utilities
   */
  async formatPhone(phone: string): Promise<PhoneFormatResponse> {
    try {
      const response = await handleApiCall<PhoneFormatResponse>(async () => {
        return await api.post({
          url: getApiUrl(endpoint.phoneUtils.format),
          body: { phone },
          auth: true,
        });
      });
      
      return response;
    } catch (error) {

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to format phone number'
      };
    }
  }

  /**
   * Get area code from phone number using backend utilities
   */
  async getAreaCode(phone: string): Promise<PhoneAreaCodeResponse> {
    try {
      const response = await handleApiCall<PhoneAreaCodeResponse>(async () => {
        return await api.get({
          url: getApiUrl(`${endpoint.phoneUtils.areaCode}?phone=${encodeURIComponent(phone)}`),
          auth: true,
        });
      });
      
      return response;
    } catch (error) {

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to extract area code'
      };
    }
  }

  /**
   * Compare two phone numbers for equality using backend utilities
   */
  async comparePhones(phone1: string, phone2: string): Promise<PhoneCompareResponse> {
    try {
      const response = await handleApiCall<PhoneCompareResponse>(async () => {
        return await api.post({
          url: getApiUrl(endpoint.phoneUtils.compare),
          body: { phone1, phone2 },
          auth: true,
        });
      });
      
      return response;
    } catch (error) {

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to compare phone numbers'
      };
    }
  }

  /**
   * Validate US phone number using backend utilities
   */
  async validateUSPhone(phone: string): Promise<PhoneValidateUSResponse> {
    try {
      const response = await handleApiCall<PhoneValidateUSResponse>(async () => {
        return await api.post({
          url: getApiUrl(endpoint.phoneUtils.validateUS),
          body: { phone },
          auth: true,
        });
      });
      
      return response;
    } catch (error) {

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to validate US phone number'
      };
    }
  }

  /**
   * Batch validate multiple phone numbers using backend utilities
   */
  async validateBatch(phones: string[]): Promise<PhoneBatchValidateResponse> {
    try {
      const response = await handleApiCall<PhoneBatchValidateResponse>(async () => {
        return await api.post({
          url: getApiUrl(endpoint.phoneUtils.validateBatch),
          body: { phones },
          auth: true,
        });
      });
      
      return response;
    } catch (error) {

      return {
        success: false,
        error: error instanceof Error ? error.message : 'Failed to validate phone numbers in batch'
      };
    }
  }

  /**
   * Check if two phone numbers are equal using backend utilities
   */
  async areEqual(phone1: string, phone2: string): Promise<boolean> {
    try {
      const response = await this.comparePhones(phone1, phone2);
      return response.success && response.data?.areEqual === true;
    } catch (error) {

      return false;
    }
  }

  /**
   * Get sanitized E.164 format phone number using backend utilities
   */
  async toE164(phone: string): Promise<string | null> {
    try {
      const response = await this.sanitizePhone(phone);
      return response.success && response.data ? response.data.sanitized : null;
    } catch (error) {

      return null;
    }
  }

  /**
   * Get formatted display phone number using backend utilities
   */
  async toDisplayFormat(phone: string): Promise<string | null> {
    try {
      const response = await this.formatPhone(phone);
      return response.success && response.data ? response.data.formatted : null;
    } catch (error) {

      return null;
    }
  }

  /**
   * Validate default outbound phone number according to project specs
   * Must be in E.164 format and exist in user's assigned Twilio numbers
   */
  async validateDefaultOutboundPhone(phone: string, userPhoneNumbers: string[]): Promise<{
    isValid: boolean;
    error?: string;
    e164?: string;
  }> {
    try {
      // First validate the phone number format
      const validation = await this.validateUSPhone(phone);
      
      if (!validation.success || !validation.data?.isValidUS) {
        return {
          isValid: false,
          error: 'Invalid US phone number format'
        };
      }

      const e164 = validation.data.sanitized;

      // Check if phone exists in user's assigned Twilio numbers
      const phoneExists = await Promise.all(
        userPhoneNumbers.map(async (userPhone) => {
          const comparison = await this.areEqual(phone, userPhone);
          return comparison;
        })
      );

      if (!phoneExists.some(exists => exists)) {
        return {
          isValid: false,
          error: 'Phone number not found in your assigned phone numbers'
        };
      }

      return {
        isValid: true,
        e164
      };
    } catch (error) {

      return {
        isValid: false,
        error: error instanceof Error ? error.message : 'Validation failed'
      };
    }
  }
}

// Export singleton instance
export const phoneUtilsService = new PhoneUtilsService();
export default phoneUtilsService;

// Export types for use in other components
export type {
  PhoneValidationResult,
  PhoneSanitizeResponse,
  PhoneValidateResponse,
  PhoneFormatResponse,
  PhoneCompareResponse,
  PhoneValidateUSResponse,
  PhoneBatchValidateResponse,
  PhoneAreaCodeResponse,
};